import React, { useEffect, useRef, useState } from "react";
import {
    Upload,
    Link as LinkIcon,
    Trash2,
    Copy,
    Check,
    Image as ImageIcon,
    Film,
    File as FileIcon,
    Plus,
    Loader2,
    Calendar,
    Download,
    X,
    Send,
    Star,
    MessageCircle,
    Clock,
    User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import { uploadFileChunked } from "@/lib/chunkedUpload";

const CHUNK_THRESHOLD = 50 * 1024 * 1024; // 50MB → use chunked uploader for big files

function fmtBytes(n) {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
    return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

function fmtDate(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function FileIconFor({ contentType, name }) {
    const t = (contentType || "").toLowerCase();
    const n = (name || "").toLowerCase();
    if (t.startsWith("image/") || /\.(jpe?g|png|gif|webp|heic)$/.test(n)) return <ImageIcon className="h-4 w-4 text-[#E5A93D]" />;
    if (t.startsWith("video/") || /\.(mp4|mov|m4v|webm|avi|mkv)$/.test(n)) return <Film className="h-4 w-4 text-[#E5A93D]" />;
    return <FileIcon className="h-4 w-4 text-zinc-500" />;
}

export default function AdminFileshare() {
    const [shares, setShares] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [activeShare, setActiveShare] = useState(null); // The currently expanded share for upload UI
    const [confirmDelete, setConfirmDelete] = useState(null); // share pending deletion confirm

    const load = async () => {
        try {
            const { data } = await api.get("/fileshare");
            setShares(data || []);
        } catch (e) {
            toast.error("Kon shares niet laden");
        }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    // If the URL contains ?share=<id> (e.g. coming from a bell notification),
    // auto-expand that share once shares are loaded.
    useEffect(() => {
        if (loading) return;
        const params = new URLSearchParams(window.location.search);
        const wantId = params.get("share");
        if (wantId) {
            const found = shares.find((s) => s.id === wantId);
            if (found) {
                setActiveShare(found);
                // Scroll into view after render
                setTimeout(() => {
                    const el = document.querySelector(`[data-testid="share-row-${wantId}"]`);
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                }, 200);
            }
        }
    }, [loading, shares]);

    const createShare = async ({ title, message, expires_in_days }) => {
        try {
            const { data } = await api.post("/fileshare", { title, message, expires_in_days });
            setShares((prev) => [data, ...prev]);
            setCreateOpen(false);
            setActiveShare(data); // immediately open the upload panel for the new share
            toast.success("Share aangemaakt — voeg nu bestanden toe");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aanmaken mislukt");
        }
    };

    const onShareUpdated = (updated) => {
        setShares((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
        if (activeShare?.id === updated.id) setActiveShare(updated);
    };

    const deleteShare = async (s) => {
        try {
            await api.delete(`/fileshare/${s.id}`);
            setShares((prev) => prev.filter((x) => x.id !== s.id));
            if (activeShare?.id === s.id) setActiveShare(null);
            setConfirmDelete(null);
            toast.success("Share verwijderd");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Verwijderen mislukt");
        }
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950" data-testid="admin-fileshare-page">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
                <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#E5A93D] mb-2">Fileshare</div>
                        <h1 className="font-display font-black text-3xl lg:text-5xl tracking-tight">Snel & eenvoudig delen</h1>
                        <p className="text-zinc-500 mt-2 text-sm">Upload video's of foto's, deel een link met klanten of familie. Shares vervallen automatisch.</p>
                    </div>
                    <Button
                        onClick={() => setCreateOpen(true)}
                        className="h-11 px-6 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98]"
                        data-testid="new-share-btn"
                    >
                        <Plus className="mr-2 h-4 w-4" /> Nieuwe share
                    </Button>
                </div>

                {loading ? (
                    <div className="text-zinc-500 text-sm">Laden…</div>
                ) : shares.length === 0 ? (
                    <EmptyState onNew={() => setCreateOpen(true)} />
                ) : (
                    <div className="space-y-3">
                        {shares.map((s) => (
                            <ShareRow
                                key={s.id}
                                share={s}
                                isActive={activeShare?.id === s.id}
                                onToggle={() => setActiveShare(activeShare?.id === s.id ? null : s)}
                                onUpdated={onShareUpdated}
                                onDelete={() => setConfirmDelete(s)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {createOpen && <CreateShareModal onCancel={() => setCreateOpen(false)} onCreate={createShare} />}
            {confirmDelete && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setConfirmDelete(null)}>
                    <div className="bg-[#FFFFFF] border border-red-500/30 rounded-md max-w-md w-full p-6 lg:p-8" onClick={(e) => e.stopPropagation()} data-testid="confirm-delete-modal">
                        <h3 className="font-display font-bold text-2xl tracking-tight mb-1">Share verwijderen?</h3>
                        <p className="text-sm text-zinc-500 mb-6">
                            "<span className="text-white">{confirmDelete.title || confirmDelete.slug}</span>" wordt definitief verwijderd. De link werkt daarna niet meer en alle bestanden worden gewist.
                        </p>
                        <div className="flex items-center gap-2">
                            <Button
                                onClick={() => deleteShare(confirmDelete)}
                                className="bg-red-500 hover:bg-red-400 text-zinc-950 font-bold rounded-sm"
                                data-testid="confirm-delete-btn"
                            >
                                Definitief verwijderen
                            </Button>
                            <button
                                onClick={() => setConfirmDelete(null)}
                                className="text-sm text-zinc-500 hover:text-zinc-950"
                                data-testid="cancel-delete-btn"
                            >
                                Annuleren
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ----------------------------------------------------------------------------
// EmptyState
// ----------------------------------------------------------------------------
function EmptyState({ onNew }) {
    return (
        <div className="border border-dashed border-zinc-200 rounded-md p-12 text-center bg-[#FFFFFF]" data-testid="fileshare-empty">
            <Send className="h-10 w-10 text-zinc-400 mx-auto mb-4" />
            <h3 className="font-display font-bold text-xl mb-2">Nog geen shares</h3>
            <p className="text-zinc-500 text-sm mb-6 max-w-md mx-auto">Maak je eerste share aan om video's of foto's te delen via een private link.</p>
            <Button onClick={onNew} className="bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm">
                <Plus className="mr-2 h-4 w-4" /> Nieuwe share aanmaken
            </Button>
        </div>
    );
}

// ----------------------------------------------------------------------------
// ShareRow — collapsable card per share
// ----------------------------------------------------------------------------
function ShareRow({ share, isActive, onToggle, onUpdated, onDelete }) {
    const [copied, setCopied] = useState(false);
    const publicUrl = `${window.location.origin}/share/${share.slug}`;
    const totalBytes = (share.files || []).reduce((sum, f) => sum + (f.size || 0), 0);
    const expired = share.expires_at && new Date(share.expires_at) < new Date();

    const copy = async (e) => {
        e.stopPropagation();
        const text = publicUrl;
        let ok = false;
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                ok = true;
            }
        } catch {
            ok = false;
        }
        if (!ok) {
            // Fallback: hidden textarea + execCommand (works inside iframes)
            try {
                const ta = document.createElement("textarea");
                ta.value = text;
                ta.setAttribute("readonly", "");
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                ta.setSelectionRange(0, text.length);
                ok = document.execCommand("copy");
                document.body.removeChild(ta);
            } catch {
                ok = false;
            }
        }
        if (ok) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
            toast.success("Link gekopieerd");
        } else {
            toast.error("Kopiëren mislukt — kopieer handmatig");
        }
    };

    return (
        <div className={`bg-[#FFFFFF] border ${expired ? "border-red-500/30" : "border-zinc-100"} rounded-md transition-colors`} data-testid={`share-row-${share.id}`}>
            <div
                role="button"
                tabIndex={0}
                onClick={onToggle}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
                className="w-full p-5 flex items-center gap-4 text-left hover:bg-zinc-100 rounded-md cursor-pointer"
                data-testid={`share-toggle-${share.id}`}
            >
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium text-zinc-950 truncate">{share.title || `Share ${share.slug}`}</div>
                        {expired && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-red-500/15 text-red-700 border border-red-500/30">Verlopen</span>}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1"><FileIcon className="h-3 w-3" /> {(share.files || []).length} bestand(en) · {fmtBytes(totalBytes)}</span>
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Verloopt {fmtDate(share.expires_at)}</span>
                        {share.download_count > 0 && <span className="flex items-center gap-1"><Download className="h-3 w-3" /> {share.download_count}× gedownload</span>}
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                        type="button"
                        onClick={copy}
                        className="inline-flex items-center gap-1.5 text-xs px-3 h-9 border border-[#E5A93D]/40 text-[#E5A93D] hover:bg-[#E5A93D]/10 rounded-sm transition active:scale-95"
                        data-testid={`share-copy-${share.id}`}
                    >
                        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                        {copied ? "Gekopieerd" : "Kopieer link"}
                    </button>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onDelete(); }}
                        className="h-9 w-9 flex items-center justify-center rounded-sm border border-red-500/30 text-red-600 hover:bg-red-500/10"
                        aria-label="Verwijderen"
                        data-testid={`share-delete-${share.id}`}
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {isActive && (
                <div className="border-t border-zinc-100 p-5 space-y-5">
                    <div className="space-y-2">
                        <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Publieke link</Label>
                        <div className="flex items-center gap-2">
                            <Input
                                readOnly
                                value={publicUrl}
                                onClick={(e) => e.target.select()}
                                className="h-10 bg-[#F4F4F5] border-zinc-200 rounded-sm text-xs font-mono text-[#E5A93D] focus-visible:border-[#E5A93D]"
                                data-testid={`share-link-input-${share.id}`}
                            />
                            <button
                                type="button"
                                onClick={copy}
                                className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs px-3 h-10 border border-[#E5A93D]/40 text-[#E5A93D] hover:bg-[#E5A93D]/10 rounded-sm transition active:scale-95"
                                data-testid={`share-copy-${share.id}-inline`}
                            >
                                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                                {copied ? "Gekopieerd" : "Kopieer"}
                            </button>
                            <a
                                href={publicUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs px-3 h-10 border border-zinc-200 hover:bg-zinc-100 text-zinc-600 rounded-sm transition"
                            >
                                <LinkIcon className="h-3.5 w-3.5" /> Open
                            </a>
                        </div>
                    </div>

                    <FileList share={share} onUpdated={onUpdated} />
                    <FileUploader shareId={share.id} onUpdated={onUpdated} />
                    <ShareFeedback share={share} />
                </div>
            )}
        </div>
    );
}


// ----------------------------------------------------------------------------
// ShareFeedback — shows comments + favorites + approvals left by viewers
// ----------------------------------------------------------------------------
function ShareFeedback({ share }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const { data: r } = await api.get(`/fileshare/${share.id}/feedback`);
            setData(r);
        } catch {
            // fail silently — the section just won't show
        }
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [share.id]);

    if (loading) return null;
    const comments = data?.comments || [];
    const reactions = data?.reactions || [];
    if (!comments.length && !reactions.length) {
        return (
            <div className="text-xs text-zinc-500 italic">Nog geen feedback ontvangen op deze share.</div>
        );
    }

    return (
        <div className="space-y-3" data-testid={`share-feedback-${share.id}`}>
            <div className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-[#E5A93D]" />
                <Label className="text-[10px] uppercase tracking-[0.2em] text-[#E5A93D] m-0">Ontvangen feedback</Label>
            </div>
            {(share.files || []).map((f, i) => {
                const fc = comments.filter(c => c.file_url === f.url);
                const fr = reactions.filter(r => r.file_url === f.url);
                const favs = fr.filter(r => r.is_favorite);
                const apps = fr.filter(r => r.is_approved);
                if (!fc.length && !favs.length && !apps.length) return null;
                return (
                    <div key={i} className="bg-[#F4F4F5] border border-zinc-100 rounded-sm p-3" data-testid={`share-feedback-file-${i}`}>
                        <div className="text-xs text-zinc-600 font-medium truncate mb-2">{f.name}</div>
                        <div className="flex items-center gap-3 flex-wrap text-[11px] text-zinc-500 mb-2">
                            {favs.length > 0 && (
                                <span className="inline-flex items-center gap-1 text-yellow-700">
                                    <Star className="h-3 w-3 fill-yellow-300" /> {favs.length} favoriet
                                    <span className="text-zinc-500 normal-case">— {favs.map(r => r.author_name).join(", ")}</span>
                                </span>
                            )}
                            {apps.length > 0 && (
                                <span className="inline-flex items-center gap-1 text-emerald-700">
                                    <Check className="h-3 w-3" /> {apps.length} goedgekeurd
                                    <span className="text-zinc-500 normal-case">— {apps.map(r => r.author_name).join(", ")}</span>
                                </span>
                            )}
                        </div>
                        {fc.length > 0 && (
                            <div className="space-y-1.5">
                                {fc.map((c) => (
                                    <div key={c.id} className="bg-[#FFFFFF] border border-zinc-100 rounded-sm px-2.5 py-1.5 text-xs">
                                        <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 mb-0.5 flex-wrap">
                                            <User className="h-2.5 w-2.5" />
                                            <span className="text-zinc-600 font-medium">{c.author_name}</span>
                                            {c.timestamp_seconds != null && (
                                                <span className="inline-flex items-center gap-0.5 font-mono text-[#E5A93D]">
                                                    <Clock className="h-2.5 w-2.5" />
                                                    {String(Math.floor(c.timestamp_seconds / 60)).padStart(2, "0")}:{String(Math.floor(c.timestamp_seconds % 60)).padStart(2, "0")}
                                                </span>
                                            )}
                                            <span className="ml-auto">{new Date(c.created_at).toLocaleString("nl-BE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                                        </div>
                                        <div className="text-zinc-800 whitespace-pre-wrap">{c.text}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}


// ----------------------------------------------------------------------------
// FileList — current files in a share, with delete
// ----------------------------------------------------------------------------
function FileList({ share, onUpdated }) {
    const [pending, setPending] = useState(null); // file pending delete-confirm

    const removeFile = async (file) => {
        try {
            await api.delete(`/fileshare/${share.id}/files`, { params: { url: file.url } });
            const { data } = await api.get(`/fileshare/${share.id}`);
            onUpdated(data);
            setPending(null);
            toast.success("Bestand verwijderd");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Verwijderen mislukt");
        }
    };

    if (!share.files?.length) {
        return <div className="text-xs text-zinc-500">Nog geen bestanden geüpload.</div>;
    }
    return (
        <>
            <div className="space-y-2" data-testid="share-file-list">
                {share.files.map((f, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-[#F4F4F5] border border-zinc-200 rounded-sm">
                        <FileIconFor contentType={f.content_type} name={f.name} />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm text-zinc-950 truncate">{f.name}</div>
                            <div className="text-[11px] text-zinc-500">{fmtBytes(f.size)} · {f.content_type || "onbekend type"}</div>
                        </div>
                        <a
                            href={f.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] text-zinc-500 hover:text-zinc-950 px-2 py-1"
                            data-testid={`share-file-open-${i}`}
                        >
                            Open
                        </a>
                        <button
                            onClick={() => setPending(f)}
                            className="h-8 w-8 flex items-center justify-center rounded-sm hover:bg-red-500/10 text-red-600"
                            aria-label="Verwijder bestand"
                            data-testid={`share-file-delete-${i}`}
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                ))}
            </div>
            {pending && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setPending(null)}>
                    <div className="bg-[#FFFFFF] border border-red-500/30 rounded-md max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-display font-bold text-xl mb-2">Bestand verwijderen?</h3>
                        <p className="text-sm text-zinc-500 mb-4 break-all">"<span className="text-white">{pending.name}</span>"</p>
                        <div className="flex items-center gap-2">
                            <button onClick={() => removeFile(pending)} className="bg-red-500 hover:bg-red-400 text-white font-bold rounded-sm h-9 px-4 text-sm" data-testid="confirm-file-delete-btn">
                                Verwijderen
                            </button>
                            <button onClick={() => setPending(null)} className="text-sm text-zinc-500 hover:text-zinc-950">
                                Annuleren
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

// ----------------------------------------------------------------------------
// FileUploader — drag & drop or button. Uses chunked upload for >50MB.
// ----------------------------------------------------------------------------
function FileUploader({ shareId, onUpdated }) {
    const [uploads, setUploads] = useState([]); // [{ name, progress, error }]
    const inputRef = useRef(null);

    const addFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;

        // Track progress per file
        const initial = files.map((f) => ({ name: f.name, progress: 0, error: null, done: false }));
        setUploads((prev) => [...prev, ...initial]);
        const startIndex = uploads.length;

        const completedFiles = [];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const idx = startIndex + i;
            try {
                const useChunked = file.size > CHUNK_THRESHOLD;
                let url, filename;
                if (useChunked) {
                    const res = await uploadFileChunked(api, file, (pct) => {
                        setUploads((prev) => prev.map((u, k) => (k === idx ? { ...u, progress: pct } : u)));
                    });
                    url = res.url;
                    filename = res.filename || file.name;
                } else {
                    // Simple POST /upload (multipart)
                    const fd = new FormData();
                    fd.append("file", file);
                    const { data } = await api.post("/upload", fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                        onUploadProgress: (evt) => {
                            const pct = evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0;
                            setUploads((prev) => prev.map((u, k) => (k === idx ? { ...u, progress: pct } : u)));
                        },
                    });
                    url = data.url;
                    filename = data.filename || file.name;
                }
                completedFiles.push({ name: file.name, url, size: file.size, content_type: file.type || null });
                setUploads((prev) => prev.map((u, k) => (k === idx ? { ...u, progress: 100, done: true } : u)));
            } catch (e) {
                setUploads((prev) => prev.map((u, k) => (k === idx ? { ...u, error: e.response?.data?.detail || "Upload mislukt" } : u)));
            }
        }

        if (completedFiles.length) {
            try {
                const { data } = await api.post(`/fileshare/${shareId}/files`, { files: completedFiles });
                onUpdated(data);
                toast.success(`${completedFiles.length} bestand(en) toegevoegd`);
            } catch (e) {
                toast.error("Bestanden uploaden gelukt maar registreren mislukt");
            }
        }

        // Auto-clear completed after 4s
        setTimeout(() => {
            setUploads((prev) => prev.filter((u) => !u.done && !u.error));
        }, 4000);
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        addFiles(e.dataTransfer.files);
    };

    return (
        <div>
            <div
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                className="border-2 border-dashed border-zinc-200 hover:border-[#E5A93D]/40 hover:bg-[#E5A93D]/[0.03] rounded-md p-8 text-center cursor-pointer transition"
                data-testid="share-dropzone"
            >
                <Upload className="h-7 w-7 text-zinc-500 mx-auto mb-2" />
                <div className="text-sm text-zinc-600">Sleep bestanden hierheen of klik om te selecteren</div>
                <div className="text-[11px] text-zinc-500 mt-1">Video's, foto's, documenten — geen maximum.</div>
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => addFiles(e.target.files)}
                    data-testid="share-file-input"
                />
            </div>

            {uploads.length > 0 && (
                <div className="mt-4 space-y-2">
                    {uploads.map((u, i) => (
                        <div key={i} className="text-xs bg-[#F4F4F5] border border-zinc-200 rounded-sm p-3">
                            <div className="flex items-center justify-between mb-1.5">
                                <span className="truncate flex items-center gap-2 text-zinc-800">
                                    {u.error ? <X className="h-3.5 w-3.5 text-red-600" /> : u.done ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Loader2 className="h-3.5 w-3.5 animate-spin text-[#E5A93D]" />}
                                    {u.name}
                                </span>
                                <span className={u.error ? "text-red-600" : "text-zinc-500"}>{u.error || `${u.progress}%`}</span>
                            </div>
                            {!u.error && (
                                <div className="h-1 bg-zinc-100 rounded-full overflow-hidden">
                                    <div className={`h-full transition-all ${u.done ? "bg-emerald-400" : "bg-[#E5A93D]"}`} style={{ width: `${u.progress}%` }} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ----------------------------------------------------------------------------
// CreateShareModal
// ----------------------------------------------------------------------------
function CreateShareModal({ onCancel, onCreate }) {
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [days, setDays] = useState(30);
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        setSubmitting(true);
        await onCreate({ title: title.trim(), message: message.trim(), expires_in_days: parseInt(days) || 30 });
        setSubmitting(false);
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onCancel}>
            <div className="bg-[#FFFFFF] border border-zinc-200 rounded-md max-w-md w-full p-6 lg:p-8" onClick={(e) => e.stopPropagation()} data-testid="create-share-modal">
                <h3 className="font-display font-bold text-2xl tracking-tight mb-1">Nieuwe share</h3>
                <p className="text-sm text-zinc-500 mb-6">Geef je share een titel en kies hoe lang de link geldig blijft.</p>
                <div className="space-y-4">
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Titel</Label>
                        <Input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="bv. Vakantievideo, Drone shots klant X…"
                            className="h-11 bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]"
                            data-testid="create-share-title"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Boodschap (optioneel)</Label>
                        <Textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={3}
                            placeholder="Eventueel een korte boodschap voor de ontvanger…"
                            className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]"
                            data-testid="create-share-message"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Vervalt na (dagen)</Label>
                        <select
                            value={days}
                            onChange={(e) => setDays(parseInt(e.target.value))}
                            className="w-full bg-[#F4F4F5] border border-zinc-200 rounded-sm h-11 px-3 text-sm text-zinc-950 focus:outline-none focus:border-[#E5A93D]"
                            data-testid="create-share-days"
                        >
                            <option value={7}>7 dagen</option>
                            <option value={14}>14 dagen</option>
                            <option value={30}>30 dagen (standaard)</option>
                            <option value={60}>60 dagen</option>
                            <option value={90}>90 dagen</option>
                            <option value={365}>1 jaar</option>
                        </select>
                    </div>
                </div>
                <div className="flex items-center gap-2 mt-6">
                    <Button onClick={submit} disabled={submitting} className="bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm" data-testid="create-share-confirm">
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aanmaken"}
                    </Button>
                    <button onClick={onCancel} className="text-sm text-zinc-500 hover:text-zinc-950">Annuleren</button>
                </div>
            </div>
        </div>
    );
}
